import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import config from '../../Config';
import Swal from 'sweetalert2';

export default function AddCategoryForm({ onCancel, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true,
    hasSubCategory: false,
  });

  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // ✅ Image Handlers
  const handleFileChange = (file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      Swal.fire('Error', 'Only image files are allowed', 'error');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      Swal.fire('Error', 'Image must be less than 2MB', 'error');
      return;
    }

    setImageFile(file);

    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(file));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFileChange(e.dataTransfer.files[0]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // ✅ Cleanup preview
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!imageFile) {
      Swal.fire('Error', 'Please upload an image', 'error');
      return;
    }

    setLoading(true);

    const token = localStorage.getItem('token');

    try {
      const formPayload = new FormData();
      formPayload.append('name', formData.name);
      formPayload.append('description', formData.description);
      formPayload.append('isActive', formData.isActive);
      formPayload.append('hasSubCategory', formData.hasSubCategory);
      formPayload.append('image', imageFile);

      const response = await fetch(`${config.apiBaseUrl}/admin/upsale/category/create`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formPayload,
      });

      const data = await response.json();

      if (response.ok) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Category added successfully!',
        }).then(() => {
          // Reset form
          setFormData({
            name: '',
            description: '',
            isActive: true,
            hasSubCategory: false,
          });
          setImageFile(null);
          setPreview('');

          onSuccess();
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: data.message || 'Failed to add category.',
        });
      }
    } catch (error) {
      console.error('Error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Something went wrong. Please try again.',
      });
    }

    setLoading(false);
  };

  return (
    <Paper sx={{ p: 4, borderRadius: 3, mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={onCancel} size="small" sx={{ mr: 1 }} disabled={loading}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6">Add New Category</Typography>
      </Box>

      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <TextField
            name="name"
            label="Category Name"
            fullWidth
            required
            value={formData.name}
            onChange={handleChange}
            disabled={loading}
          />

          <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            sx={{
              border: '2px dashed #ccc',
              borderRadius: 2,
              p: 2,
              textAlign: 'center',
              bgcolor: '#fafafa',
              height: 220,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <input
              type="file"
              hidden
              id="category-image-upload"
              accept="image/*"
              onChange={(e) => handleFileChange(e.target.files[0])}
            />

            {!preview ? (
              <label htmlFor="category-image-upload" style={{ cursor: 'pointer' }}>
                <Typography>
                  Drag & Drop or <b>Click to Upload</b>
                </Typography>
              </label>
            ) : (
              <>
                <Box
                  sx={{
                    width: '100%',
                    height: 120,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1,
                  }}
                >
                  <img
                    src={preview}
                    alt="preview"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </Box>

                <Stack direction="row" spacing={1}>
                  <Button component="label" size="small">
                    Replace
                    <input
                      hidden
                      type="file"
                      onChange={(e) => handleFileChange(e.target.files[0])}
                    />
                  </Button>

                  <Button
                    color="error"
                    size="small"
                    onClick={() => {
                      setImageFile(null);
                      setPreview('');
                    }}
                  >
                    Remove
                  </Button>
                </Stack>
              </>
            )}
          </Box>

          <TextField
            name="description"
            label="Description"
            multiline
            rows={3}
            fullWidth
            required
            value={formData.description}
            onChange={handleChange}
            disabled={loading}
          />

          <FormControlLabel
            control={
              <Checkbox
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                disabled={loading}
              />
            }
            label="Is Active"
          />

          <FormControlLabel
            control={
              <Checkbox
                name="hasSubCategory"
                checked={formData.hasSubCategory}
                onChange={handleChange}
                disabled={loading}
              />
            }
            label="Has Subcategory"
          />

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>

            <Button variant="contained" type="submit" disabled={loading || !imageFile}>
              {loading ? <CircularProgress size={22} color="inherit" /> : 'Submit'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
}
